import { Locale } from './locale';
import { I18nLabels } from './common';

/**
 * 通用任务状态枚举
 * 适用于所有异步任务（图片生成、视频生成、音频生成、对口型等）
 */
export enum TaskStatus {
  PENDING = 'pending',       // 等待中（未提交）
  SUBMITTED = 'submitted',   // 已提交到外部API
  PROCESSING = 'processing', // 处理中
  COMPLETED = 'completed',   // 已完成
  FAILED = 'failed'         // 失败
}

/**
 * 通用任务状态标签
 */
export const TaskStatusLabels: I18nLabels<TaskStatus> = {
  [TaskStatus.PENDING]: { [Locale.ZH_CN]: '等待中', [Locale.EN_US]: 'Pending' },
  [TaskStatus.SUBMITTED]: { [Locale.ZH_CN]: '已提交', [Locale.EN_US]: 'Submitted' },
  [TaskStatus.PROCESSING]: { [Locale.ZH_CN]: '处理中', [Locale.EN_US]: 'Processing' },
  [TaskStatus.COMPLETED]: { [Locale.ZH_CN]: '已完成', [Locale.EN_US]: 'Completed' },
  [TaskStatus.FAILED]: { [Locale.ZH_CN]: '失败', [Locale.EN_US]: 'Failed' },
};

/**
 * 工作流状态枚举
 */
export enum WorkflowStatus {
  PENDING = 'pending',
  RUNNING = 'running',
  COMPLETED = 'completed',
  FAILED = 'failed',
  CANCELLED = 'cancelled'
}

/**
 * 工作流状态标签
 */
export const WorkflowStatusLabels: I18nLabels<WorkflowStatus> = {
  [WorkflowStatus.PENDING]: { [Locale.ZH_CN]: '等待中', [Locale.EN_US]: 'Pending' },
  [WorkflowStatus.RUNNING]: { [Locale.ZH_CN]: '运行中', [Locale.EN_US]: 'Running' },
  [WorkflowStatus.COMPLETED]: { [Locale.ZH_CN]: '已完成', [Locale.EN_US]: 'Completed' },
  [WorkflowStatus.FAILED]: { [Locale.ZH_CN]: '失败', [Locale.EN_US]: 'Failed' },
  [WorkflowStatus.CANCELLED]: { [Locale.ZH_CN]: '已取消', [Locale.EN_US]: 'Cancelled' },
};

/**
 * 工作流步骤状态枚举
 */
export enum WorkflowStepStatus {
  PENDING = 'pending',
  EXECUTING = 'executing',
  COMPLETED = 'completed',
  FAILED = 'failed',
  SKIPPED = 'skipped'
}

/**
 * 工作流步骤状态标签
 */
export const WorkflowStepStatusLabels: I18nLabels<WorkflowStepStatus> = {
  [WorkflowStepStatus.PENDING]: { [Locale.ZH_CN]: '等待中', [Locale.EN_US]: 'Pending' },
  [WorkflowStepStatus.EXECUTING]: { [Locale.ZH_CN]: '执行中', [Locale.EN_US]: 'Executing' },
  [WorkflowStepStatus.COMPLETED]: { [Locale.ZH_CN]: '已完成', [Locale.EN_US]: 'Completed' },
  [WorkflowStepStatus.FAILED]: { [Locale.ZH_CN]: '失败', [Locale.EN_US]: 'Failed' },
  [WorkflowStepStatus.SKIPPED]: { [Locale.ZH_CN]: '已跳过', [Locale.EN_US]: 'Skipped' },
};

/**
 * 类型守卫：检查是否为有效的任务状态
 */
export function isValidTaskStatus(status: string): status is TaskStatus {
  return Object.values(TaskStatus).includes(status as TaskStatus);
}

/**
 * 类型守卫：检查是否为有效的工作流状态
 */
export function isValidWorkflowStatus(status: string): status is WorkflowStatus {
  return Object.values(WorkflowStatus).includes(status as WorkflowStatus);
}

/**
 * 类型守卫：检查是否为有效的工作流步骤状态
 */
export function isValidWorkflowStepStatus(status: string): status is WorkflowStepStatus {
  return Object.values(WorkflowStepStatus).includes(status as WorkflowStepStatus);
}

/**
 * 检查任务是否处于终态
 */
export function isTaskTerminalStatus(status: TaskStatus): boolean {
  return status === TaskStatus.COMPLETED || status === TaskStatus.FAILED;
}

/**
 * 检查工作流是否处于终态
 */
export function isWorkflowTerminalStatus(status: WorkflowStatus): boolean {
  return status === WorkflowStatus.COMPLETED ||
    status === WorkflowStatus.FAILED ||
    status === WorkflowStatus.CANCELLED;
}
